import { useState } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

/** Cor do pontinho de cada marcador. A chave é livre — quem usa decide o que marcar. */
export type CalendarMarkers = Record<string, string[]>;

interface SimpleCalendarProps {
    selectedDate?: Date | null;
    onDateChange: (date: Date) => void;
    /**
     * Pontinhos sob o número do dia: `{ '2026-08-19': ['#f59e0b', '#22c55e'] }`.
     *
     * Existe para o calendário poder DIZER onde há algo antes de o usuário clicar. Sem isso, achar
     * o dia certo é tentativa e erro — abre, vê que está vazio, volta.
     */
    markers?: CalendarMarkers;
    /** chamado ao navegar de mês, para quem precisa buscar os marcadores daquele período */
    onMonthChange?: (year: number, month: number) => void;
}

/** `Date` → `YYYY-MM-DD` no fuso LOCAL. `toISOString` daria o dia errado à noite no Brasil. */
export const dayKey = (date: Date): string =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

const SimpleCalendar = ({ selectedDate, onDateChange, markers, onMonthChange }: SimpleCalendarProps) => {
    const [currentMonth, setCurrentMonth] = useState(selectedDate || new Date());

    const goToMonth = (next: Date) => {
        setCurrentMonth(next);
        onMonthChange?.(next.getFullYear(), next.getMonth());
    };

    const today = new Date();
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();

    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const days = [];
    const current = new Date(startDate);

    for (let i = 0; i < 42; i++) {
        days.push(new Date(current));
        current.setDate(current.getDate() + 1);
    }

    const monthNames = [
        'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
        'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];

    const weekDays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

    const isToday = (date: Date) => {
        return date.toDateString() === today.toDateString();
    };

    const isSelected = (date: Date) => {
        if (!selectedDate) return false;
        return date.getFullYear() === selectedDate.getFullYear() &&
            date.getMonth() === selectedDate.getMonth() &&
            date.getDate() === selectedDate.getDate();
    };

    const isCurrentMonth = (date: Date) => {
        return date.getMonth() === month;
    };

    return (
        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg p-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <button
                    onClick={() => goToMonth(new Date(year, month - 1))}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                >
                    <FiChevronLeft size={16} />
                </button>
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                    {monthNames[month]} {year}
                </span>
                <button
                    onClick={() => goToMonth(new Date(year, month + 1))}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded"
                >
                    <FiChevronRight size={16} />
                </button>
            </div>

            {/* Week days */}
            <div className="grid grid-cols-7 gap-1 mb-2">
                {weekDays.map(day => (
                    <div key={day} className="text-xs text-gray-500 dark:text-gray-400 text-center py-1">
                        {day}
                    </div>
                ))}
            </div>

            {/* Days */}
            <div className="grid grid-cols-7 gap-1">
                {days.map((date, index) => {
                    const dots = markers?.[dayKey(date)] || [];
                    return (
                        <button
                            key={index}
                            onClick={() => {
                                // Criar nova data no timezone local para evitar problemas de fuso horário
                                const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
                                onDateChange(localDate);
                            }}
                            className={`
                                text-xs p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors
                                ${!isCurrentMonth(date) ? 'text-gray-300 dark:text-gray-600' : 'text-gray-900 dark:text-white'}
                                ${isToday(date) ? 'bg-blue-100 dark:bg-blue-900 font-medium' : ''}
                                ${isSelected(date) ? 'bg-blue-600 text-white hover:bg-blue-700' : ''}
                            `}
                        >
                            {date.getDate()}
                            {/* altura reservada SEMPRE que há marcadores no mês, senão a grade
                                "pula" de altura conforme os dias ganham ou perdem pontinho */}
                            {markers && (
                                <span className="flex justify-center gap-0.5 h-1.5 mt-0.5">
                                    {dots.map(color => (
                                        <span key={color} className="w-1 h-1 rounded-full"
                                            style={{ backgroundColor: color }} />
                                    ))}
                                </span>
                            )}
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default SimpleCalendar;
